/**
 * Flavor text: GCPPA notifications and company taunts.
 * Pure data + helpers, no DOM or side effects.
 */

// =============================================================================
// GCPPA MESSAGES — council notifications (gold/amber)
// =============================================================================

const GCPPA_DEATH: string[] = [
  'Contractor {name} has been disqualified from assessment.',
  '{name} eliminated. GCPPA reminds remaining contractors: collateral happens.',
  '{name} removed from active roster. Equipment retrieval form pending.',
];

const GCPPA_GAME_OVER: string[] = [
  'Conservation contract awarded to {name}. All other bids rejected.',
  '{name} declared sole surviving bidder. Contract formalized under duress.',
];

const GCPPA_DRAW: string[] = [
  'All contractors eliminated. Contract remains unawarded. Budget reallocated.',
];

const gcppaTemplates: Record<string, string[]> = {
  death: GCPPA_DEATH,
  gameOver: GCPPA_GAME_OVER,
  draw: GCPPA_DRAW,
};

/** The same events said plainly (flavor text off). */
const plainTemplates: Record<string, string[]> = {
  death: ['{name} eliminated.'],
  gameOver: ['{name} wins the match.'],
  draw: ['Draw — no one left standing.'],
};

export function getGcppaMessage(
  type: string,
  vars: Record<string, string>,
  rng?: { next(): number },
  plain = false,
): string {
  const templates = (plain ? plainTemplates : gcppaTemplates)[type];
  if (!templates || templates.length === 0) return '';
  const roll = rng ? rng.next() : Math.random();
  const idx = Math.floor(roll * templates.length);
  let msg = templates[idx];
  for (const [key, val] of Object.entries(vars)) {
    msg = msg.replace(`{${key}}`, val);
  }
  return msg;
}

// =============================================================================
// TEAM TAUNTS — per-company smack talk (indexed by team)
// =============================================================================

export const TEAM_TAUNTS: string[][] = [
  // 0: Sidereal Conservancy
  [
    'We protect stars. You ARE the collateral.',
    'Another day, another assessment we win on merit.',
    'Our impact studies predicted this outcome.',
  ],
  // 1: Crater & Bloom
  [
    'We reshape worlds. You just got reshaped.',
    'That crater? Professionally placed.',
    'Bloom follows the crater. Remember that.',
  ],
  // 2: TerraGently Inc.
  [
    'Sustainability includes sustaining our lead.',
    'Nothing personal. Our shareholders insisted.',
    'Gentle is a brand name, not a promise.',
  ],
  // 3: GreenOrbit Solutions
  [
    'Optimized that outcome in under three seconds.',
    'Your trajectory was suboptimal. Ours wasn\'t.',
    'Ecological outcomes: optimized. Your health: not.',
  ],
  // 4: BioSphere Direct
  [
    'Conservation. Delivered. To your face.',
    'Direct hit, direct delivery, direct results.',
    'We don\'t miss. It\'s in the mission statement.',
  ],
  // 5: AstraTurf Co.
  [
    'If it\'s not green, we make it green. With explosions.',
    'Turf: reclaimed.',
    'We put the "astro" in "catastrophe."',
  ],
  // 6: Gentle Futures Group
  [
    'Our projections said you\'d lose. They were right.',
    'Investing in tomorrow. You won\'t be there.',
    'That was a future-positive outcome. For us.',
  ],
  // 7: PlanetCare Ltd.
  [
    'Reliable. Predictable. Devastating.',
    'Standard operating procedure. Don\'t take it personally.',
    'We care about planets. People are secondary.',
  ],
  // 8: Orbital Reclamation Bureau
  [
    'Authorized by the GCPPA. Mostly.',
    'Reclamation in progress. Stand clear.',
    'Your zone has been reclaimed. You\'re welcome.',
  ],
  // 9: EcoShield LLC
  [
    'Aggressive environmental defense in action.',
    'Shield up. You\'re down.',
    'We defend the ecosystem. From you.',
  ],
];

export function getRandomTaunt(
  teamIndex: number,
  rng?: { next(): number }
): string {
  const taunts = TEAM_TAUNTS[teamIndex % TEAM_TAUNTS.length];
  if (!taunts || taunts.length === 0) return '';
  const roll = rng ? rng.next() : Math.random();
  return taunts[Math.floor(roll * taunts.length)];
}
